import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './components/Sidebar';
import ChatWindow from './components/ChatWindow';
import LoginPage from './components/LoginPage';
import useGeolocation from './hooks/useGeolocation';
import useSessions from './hooks/useSessions';
import './App.css';

function App() {
  const [email, setEmail] = useState<string | null>(() => localStorage.getItem('ev_user_email') || null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth <= 768);
  const initialSessionCreated = useRef(false);

  const { location, locationError, requestLocation } = useGeolocation();
  const {
    sessions,
    activeSessionId,
    sessionsLoaded,
    selectSession,
    createSession,
    addMessage,
    getActiveSession,
    deleteSession,
  } = useSessions(email);

  useEffect(() => {
    console.log('[App] App mounted. Requesting geolocation...');
    requestLocation();
  }, []);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSidebarCollapsed(e.matches);
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    // Mobile browsers report `100vh`/`100dvh` inconsistently as the address bar
    // shows/hides, which can push fixed-position content (like the sidebar)
    // behind the browser chrome. Track the real visible height in JS instead.
    const setAppHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    setAppHeight();
    window.addEventListener('resize', setAppHeight);
    window.addEventListener('orientationchange', setAppHeight);
    window.visualViewport?.addEventListener('resize', setAppHeight);
    return () => {
      window.removeEventListener('resize', setAppHeight);
      window.removeEventListener('orientationchange', setAppHeight);
      window.visualViewport?.removeEventListener('resize', setAppHeight);
    };
  }, []);

  useEffect(() => {
    if (!email) {
      initialSessionCreated.current = false;
      return;
    }
    if (!sessionsLoaded) return;
    if (initialSessionCreated.current) return;

    initialSessionCreated.current = true;
    if (Object.keys(sessions).length === 0) {
      console.log('[App] No existing sessions found. Creating initial session...');
      createSession();
    } else {
      console.log('[App] Existing sessions found:', Object.keys(sessions).length);
    }
  }, [email, sessionsLoaded, sessions]);

  useEffect(() => {
    if (location) {
      console.log('[App] Location updated:', location);
    }
    if (locationError) {
      console.warn('[App] Location error:', locationError);
    }
  }, [location, locationError]);

  useEffect(() => {
    if (activeSessionId) {
      console.log('[App] Active session changed to:', activeSessionId);
    }
  }, [activeSessionId]);

  const handleLogin = (userEmail: string) => {
    localStorage.setItem('ev_user_email', userEmail);
    setEmail(userEmail);
  };

  const handleLogout = () => {
    console.log('[App] User logged out:', email);
    localStorage.removeItem('ev_user_email');
    setEmail(null);
  };

  const activeSession = getActiveSession();

  if (!email) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      <Sidebar
        sessions={sessions}
        activeSessionId={activeSessionId}
        collapsed={sidebarCollapsed}
        onNewChat={createSession}
        onSelectSession={selectSession}
        onDeleteSession={deleteSession}
        onLogout={handleLogout}
        onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
        email={email}
      />
      <ChatWindow
        session={activeSession}
        location={location}
        locationError={locationError}
        onAddMessage={addMessage}
        email={email}
        sidebarCollapsed={sidebarCollapsed}
        onExpandSidebar={() => setSidebarCollapsed(false)}
      />
    </div>
  );
}

export default App;
